/**
 * CurveObject - Renders curve geometry in Three.js
 *
 * Supports Line, Polyline, Arc, Circle
 */

import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { GEOMETRY_COLORS } from '../../utils/constants';

const Z_CURVE = 5; // Z-level for curves
const HIT_WIDTH = 200; // Hit area width in mm (20cm each side)

/**
 * Render a single curve
 */
export function CurveObject({
  curve,
  isSelected = false,
  isHovered = false,
  onClick,
  onPointerOver,
  onPointerOut,
}) {
  const meshRef = useRef();

  // Get display points for the curve
  const points = useMemo(() => {
    if (!curve) return [];

    // Get points from curve (use more segments for arcs/circles)
    const segments = curve.type === 'Line' ? 1 : 32;
    return curve.getDisplayPoints(segments);
  }, [curve]);

  const positions = useMemo(() => {
    if (points.length === 0) return new Float32Array(0);

    const arr = new Float32Array(points.length * 3);
    points.forEach((p, i) => {
      arr[i * 3] = p.x;
      arr[i * 3 + 1] = p.y;
      arr[i * 3 + 2] = Z_CURVE;
    });
    return arr;
  }, [points]);

  // Create hit geometry (wider invisible area for clicking)
  const hitGeometry = useMemo(() => {
    if (points.length < 2) return null;

    const shape = new THREE.Shape();
    const halfWidth = HIT_WIDTH;

    // Build a path that follows the curve with width
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      // Direction perpendicular to segment
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = -dy / len * halfWidth;
      const ny = dx / len * halfWidth;

      if (i === 0) {
        shape.moveTo(p1.x + nx, p1.y + ny);
      }
      shape.lineTo(p2.x + nx, p2.y + ny);
    }

    // Go back along the other side
    for (let i = points.length - 1; i > 0; i--) {
      const p1 = points[i];
      const p2 = points[i - 1];

      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = -dy / len * halfWidth;
      const ny = dx / len * halfWidth;

      shape.lineTo(p1.x + nx, p1.y + ny);
    }

    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [points]);

  // Determine color based on type and state
  const color = useMemo(() => {
    if (isSelected) return GEOMETRY_COLORS.selected;
    if (isHovered) return GEOMETRY_COLORS.hover;

    // Construction lines are gray
    if (curve?.attributes?.isConstruction) {
      return GEOMETRY_COLORS.construction;
    }

    switch (curve?.type) {
      case 'Line':
      case 'Polyline':
        return GEOMETRY_COLORS.line;
      case 'Arc':
      case 'Circle':
        return GEOMETRY_COLORS.arc;
      default:
        return GEOMETRY_COLORS.line;
    }
  }, [curve?.type, curve?.attributes?.isConstruction, isSelected, isHovered]);

  const lineWidth = isSelected ? 3 : isHovered ? 2 : 1;

  // Create filled shape geometry for closed polylines
  const fillGeometry = useMemo(() => {
    if (!curve || curve.type !== 'Polyline' || !curve.closed) return null;
    if (points.length < 3) return null;

    const shape = new THREE.Shape();
    shape.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      // Skip the closing duplicate point (last point == first point)
      if (i === points.length - 1 && points[i].x === points[0].x && points[i].y === points[0].y) continue;
      shape.lineTo(points[i].x, points[i].y);
    }
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [curve, points]);

  if (!curve || positions.length < 6) return null;

  return (
    <group>
      {/* Filled surface for closed polylines */}
      {fillGeometry && (
        <mesh
          geometry={fillGeometry}
          position={[0, 0, Z_CURVE - 2]}
          onClick={onClick}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
        >
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.15}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {/* Invisible hit area for click detection */}
      {hitGeometry && !fillGeometry && (
        <mesh
          geometry={hitGeometry}
          position={[0, 0, Z_CURVE - 1]}
          onClick={onClick}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
        >
          <meshBasicMaterial transparent opacity={0} />
        </mesh>
      )}

      {/* Visible line */}
      <line ref={meshRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={positions.length / 3}
            array={positions}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial
          color={color}
          linewidth={lineWidth}
          transparent
          opacity={0.9}
        />
      </line>
    </group>
  );
}

/**
 * Render all curves
 */
export function CurveObjects({
  curves = [],
  selectedIds = [],
  hoveredId = null,
  onCurveClick,
  onCurveHover,
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <group name="curves">
      {curves.map(curve => (
        <CurveObject
          key={curve.id}
          curve={curve}
          isSelected={selectedSet.has(curve.id)}
          isHovered={hoveredId === curve.id}
          onClick={(e) => {
            e.stopPropagation();
            onCurveClick?.(curve.id, e);
          }}
          onPointerOver={(e) => {
            e.stopPropagation();
            onCurveHover?.(curve.id);
          }}
          onPointerOut={() => {
            onCurveHover?.(null);
          }}
        />
      ))}
    </group>
  );
}

export default CurveObject;
